import { ANIMAL_KINDS, type AnimalKind } from "../game/animals";
import type { PersistedRecord } from "../persistence";

export interface AnimalMetrics {
  readonly sessions: number;
  readonly playSeconds: number;
}

export interface MetricsSnapshot {
  readonly sessionsStarted: number;
  readonly totalPlaySeconds: number;
  readonly perAnimal: Readonly<Record<AnimalKind, AnimalMetrics>>;
}

const ZERO: AnimalMetrics = { sessions: 0, playSeconds: 0 };

export function defaultMetrics(): MetricsSnapshot {
  const perAnimal: Record<AnimalKind, AnimalMetrics> = {
    seal: { ...ZERO },
    penguin: { ...ZERO },
    fish: { ...ZERO },
  };
  return {
    sessionsStarted: 0,
    totalPlaySeconds: 0,
    perAnimal,
  };
}

function isNonNegativeFinite(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value) && value >= 0;
}

function isAnimalMetrics(value: unknown): value is AnimalMetrics {
  if (typeof value !== "object" || value === null) return false;
  const v = value as Record<string, unknown>;
  return isNonNegativeFinite(v.sessions) && isNonNegativeFinite(v.playSeconds);
}

function isPerAnimal(value: unknown): value is Record<AnimalKind, AnimalMetrics> {
  if (typeof value !== "object" || value === null) return false;
  const v = value as Record<string, unknown>;
  return ANIMAL_KINDS.every((animal) => isAnimalMetrics(v[animal]));
}

export function isMetricsSnapshot(value: unknown): value is MetricsSnapshot {
  if (typeof value !== "object" || value === null) return false;
  const v = value as Record<string, unknown>;
  return (
    isNonNegativeFinite(v.sessionsStarted) &&
    isNonNegativeFinite(v.totalPlaySeconds) &&
    isPerAnimal(v.perAnimal)
  );
}

export function mostPlayedAnimal(snapshot: MetricsSnapshot): AnimalKind | null {
  let leader: AnimalKind | null = null;
  let bestSeconds = 0;
  let bestSessions = 0;
  for (const animal of ANIMAL_KINDS) {
    const entry = snapshot.perAnimal[animal];
    if (entry.playSeconds === 0 && entry.sessions === 0) continue;
    if (leader === null) {
      leader = animal;
      bestSeconds = entry.playSeconds;
      bestSessions = entry.sessions;
      continue;
    }
    if (
      entry.playSeconds > bestSeconds ||
      (entry.playSeconds === bestSeconds && entry.sessions > bestSessions)
    ) {
      leader = animal;
      bestSeconds = entry.playSeconds;
      bestSessions = entry.sessions;
    }
  }
  return leader;
}

export const METRICS_RECORD: PersistedRecord<MetricsSnapshot> = {
  key: "metrics",
  version: 1,
  defaults: defaultMetrics,
  validate: isMetricsSnapshot,
  migrations: {},
};
